import type { Vault as VaultHandle, AgentManifest } from '@cef-ai/vault-sdk';
import type { AgentConnectionHandle } from '@cef-ai/vault-sdk';
import { getAsPubkey } from '@cef-ai/vault-sdk';

// WHY THIS EXISTS (it is not a passthrough for `vault.agents.connect`):
// it translates a `manifest` into the two wire fields the SDK actually
// takes, and fails closed when that translation cannot be done safely.
//
// vault-sdk 3.0.0 resolves the manifest itself from `agentId` (vault-api
// reads it from the DDC registry), so `version`/`manifestCid` are no longer
// sent. Callers still hold a full `AgentManifest` (see VaultConnectFlow.tsx),
// but only `agentId` and the AS pubkey reach the wire — and the AS pubkey is
// the part that needs care: `getAsPubkey` reads it across the manifest's
// three known field spellings (`agentServicePubkey`, `asPubkey`, `asPubKey`).
// When none is present we throw rather than omit the field, because omitting
// it makes the SDK derive the key from the `agentId` prefix on the assumption
// that it is `<pubkey>:<alias>` — a shape this repo's agentIds do not
// reliably follow, so the fallback would connect the agent under the WRONG
// service key instead of failing.
//
// The SDK still owns the crypto: it builds and signs the GAR consent
// agreement internally and POSTs `/agents` with the signature, replacing the
// old two-call `agreements.sign` + `agents.connect` flow.
//
// An arguments object rather than positional params: it keeps the two
// same-typed strings (`scope` and the manifest's id) from being swappable at
// the call site, and gives the seam one mockable shape. Mirrors
// `vault-delegate.ts`.
export interface ConnectAgentInput {
  vault: VaultHandle;
  manifest: AgentManifest;
  scope: string;
  settings?: Record<string, unknown>;
}

export async function signAndConnectAgent({
  vault,
  manifest,
  scope,
  settings,
}: ConnectAgentInput): Promise<AgentConnectionHandle> {
  const agentServicePubkey = getAsPubkey(manifest);
  if (!agentServicePubkey) {
    throw new Error(
      `Cannot connect agent "${manifest.agentId}": manifest has no agent-service pubkey ` +
        `(checked agentServicePubkey, asPubkey, asPubKey).`
    );
  }
  return vault.agents.connect({
    agentId: manifest.agentId,
    agentServicePubkey,
    scope,
    settings,
  });
}
